import { FC, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable from '@/components/functional/DataTable/DataTable.tsx';
import { createColumnHelper } from '@tanstack/react-table';
import supabaseClient from '@/api/supabaseClient.ts';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from 'react-daisyui';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import Routes from '@/router/Routes';

interface UserResponse {
  subdomain_id: number;
  selected_level: number;
}

interface ReportRow {
  id: number;
  domain_name: string;
  date_completed: string | null;
  quick_view_url: string | null;
  full_summary_url: string | null;
  recommendations_url: string | null;
  isCompleted: boolean;
  user_assessment_id?: string;
  total_subdomains?: number;
  completed_responses?: number;
  average_score?: number;
}

const columnHelper = createColumnHelper<ReportRow>();

const ChildReportsPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const [searchParams] = useSearchParams();
  
  // Get user info from URL params first, fallback to fetching if not available
  const nameFromUrl = searchParams.get('name');
  const emailFromUrl = searchParams.get('email');
  const authIdFromUrl = searchParams.get('authId');
  
  const [childUserInfo, setChildUserInfo] = useState<{ name: string; email: string } | null>(
    nameFromUrl && emailFromUrl ? { name: nameFromUrl, email: emailFromUrl } : null
  );
  const [reportsData, setReportsData] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(true);

  const loadChildUserInfo = useCallback(async () => {
    // If we already have info from URL, no need to fetch
    if (nameFromUrl && emailFromUrl) {
      setChildUserInfo({ name: nameFromUrl, email: emailFromUrl });
      return;
    }
    
    if (!userId) return;

    try {
      const { data: userInfo, error } = await supabaseClient
        .from('users')
        .select('name')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error loading child user info:', error);
        return;
      }

      if (userInfo) {
        setChildUserInfo({
          name: userInfo.name || 'Unknown',
          email: emailFromUrl || ''
        });
      }
    } catch (error) {
      console.error('Error loading child user info:', error);
    }
  }, [userId, nameFromUrl, emailFromUrl]);

  const loadReportsData = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);
      
      console.log('🔍 Loading reports for userId:', userId);
      
      // Use authId from URL params if available (bypasses RLS issues)
      let userAuthId = authIdFromUrl;
      
      if (!userAuthId) {
        // Fallback: try to get auth_id from users table
        const { data: userData, error: userError } = await supabaseClient
          .from('users')
          .select('auth_id, name, organization_name')
          .eq('id', userId)
          .maybeSingle();

        if (userError) {
          console.error('❌ Error loading user data:', userError);
          setLoading(false);
          return;
        }

        if (!userData) {
          console.log('⚠️ User not found:', userId);
          setReportsData([]);
          setLoading(false);
          return;
        }

        userAuthId = userData.auth_id;
      }

      console.log('✅ Using auth_id:', userAuthId);

      // Get all domains with subdomains
      const { data: domains, error: domainsError } = await supabaseClient
        .from('domains')
        .select(`
          id,
          name,
          subdomains (
            id,
            name
          )
        `)
        .order('id');

      if (domainsError) {
        console.error('Error loading domains:', domainsError);
        setLoading(false);
        return;
      }

      // Get completed assessments for this user with user_responses
      const { data: completedAssessments, error: assessmentsError } = await supabaseClient
        .from('user_assessments')
        .select(`
          id,
          user_id,
          assessment_name,
          status,
          started_at,
          completed_at,
          created_at,
          updated_at,
          user_responses (
            id,
            user_assessment_id,
            subdomain_id,
            selected_level,
            created_at,
            updated_at
          )
        `)
        .eq('user_id', userAuthId)
        .eq('status', 'completed')
        .order('completed_at', { ascending: false });

      if (assessmentsError) {
        console.error('Error loading assessments:', assessmentsError);
        setLoading(false);
        return;
      }

      console.log('✅ Loaded user_assessments from database:', completedAssessments);
      console.log('📊 Number of completed assessments:', completedAssessments?.length || 0);

      const tableData: ReportRow[] = [];

      // Build table data with detailed information
      for (const assessment of completedAssessments || []) {
        console.log('📋 Processing assessment:', {
          id: assessment.id,
          name: assessment.assessment_name,
          status: assessment.status,
          completed_at: assessment.completed_at,
          user_responses_count: assessment.user_responses?.length || 0
        });

        const domain = domains?.find(d => d.name === assessment.assessment_name);
        
        if (domain) {
          const responses = (assessment.user_responses || []) as UserResponse[];
          const totalSubdomains = (domain.subdomains || []).length;
          const completedResponses = responses.length;
          
          // Calculate average score
          let averageScore = 0;
          if (responses.length > 0) {
            const totalScore = responses.reduce((sum, r) => sum + (r.selected_level || 0), 0);
            const maxPossibleScore = responses.length * 6; // Max level is 6
            averageScore = maxPossibleScore > 0 ? Math.round((totalScore / maxPossibleScore) * 100) : 0;
          }

          console.log('📊 Assessment stats:', {
            domain: domain.name,
            total_subdomains: totalSubdomains,
            completed_responses: completedResponses,
            average_score: averageScore
          });

          tableData.push({
            id: domain.id,
            domain_name: domain.name,
            date_completed: assessment.completed_at,
            quick_view_url: 'Quick View',
            full_summary_url: 'Report Summary',
            recommendations_url: 'Recommendations & Action Steps',
            isCompleted: true,
            user_assessment_id: assessment.id,
            total_subdomains: totalSubdomains,
            completed_responses: completedResponses,
            average_score: averageScore
          });
        } else {
          console.warn('⚠️ Domain not found for assessment:', assessment.assessment_name);
        }
      }

      console.log('✅ Final table data prepared:', tableData.length, 'rows');
      setReportsData(tableData);
    } catch (error) {
      console.error('❌ Error loading reports data:', error);
    } finally {
      setLoading(false);
    }
  }, [userId, authIdFromUrl]);

  useEffect(() => {
    void loadChildUserInfo();
    void loadReportsData();
  }, [loadChildUserInfo, loadReportsData]);

  const handleQuickViewClick = async (row: ReportRow) => {
    navigate(`/assessment-results/${row.user_assessment_id}?userId=${userId}`);
  };

  const handleReportSummaryClick = (row: ReportRow) => {
    navigate(`/reports/domain-recommendations/${row.id}?assessmentId=${row.user_assessment_id}&userId=${userId}`);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB'); 
  };

  const columns = [
    columnHelper.accessor('domain_name', {
      cell: (info) => <span className='font-semibold'>{info.getValue()}</span>,
      header: () => <span>{t('reports.category')}</span>,
      footer: (info) => info.column.id,
      enableSorting: false,
    }),
    columnHelper.accessor('date_completed', {
      cell: (info) => (
        <span className='text-sm text-gray-900'>
          {formatDate(info.getValue())}
        </span>
      ),
      header: () => <span>{t('reports.dateCompleted')}</span>,
      footer: (info) => info.column.id,
      enableSorting: false,
    }),
    columnHelper.accessor('average_score', {
      cell: (info) => (
        <span className='text-sm font-semibold text-gray-900'>
          {info.getValue() !== undefined ? `${info.getValue()}%` : '-'}
        </span>
      ),
      header: () => <span>Score</span>,
      footer: (info) => info.column.id,
      enableSorting: false,
    }),
    columnHelper.accessor('quick_view_url', {
      cell: (info) => (
        <button
          onClick={() => handleQuickViewClick(info.row.original)}
          className='text-black font-medium text-sm underline'
        >
          {info.getValue()}
        </button>
      ),
      header: () => <span>{t('reports.quickView')}</span>,
      footer: (info) => info.column.id,
      enableSorting: false,
    }),
    columnHelper.accessor('full_summary_url', {
      cell: (info) => (
        <button
          onClick={() => handleReportSummaryClick(info.row.original)}
          className='text-black  font-medium text-sm underline'
        >
          {info.getValue()}
        </button>
      ),
      header: () => <span>{t('reports.fullSummary')}</span>,
      footer: (info) => info.column.id,
      enableSorting: false,
    }),
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex items-center gap-4 mb-4">
        <Button
          color="ghost"
          size="sm"
          startIcon={<ArrowLeftIcon className="w-4 h-4" />}
          onClick={() => navigate(Routes.ORGANIZATIONS)}
        >
          Back to Organizations
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 mb-4">
        <h1 className="text-2xl font-semibold text-gray-900">
          Reports for {childUserInfo?.email}
        </h1>
        {/* <p className="text-gray-600">{childUserInfo?.email}</p> */}
      </div>

      {reportsData.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <p className="text-gray-600 mb-4">{t('reports.noCompletedAssessments')}</p>
          <p className="text-sm text-gray-500">This user hasn't completed any assessments yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
          <DataTable 
            data={reportsData} 
            columns={columns} 
            className="[&_tbody_tr:nth-child(odd)]:bg-table-stripe overflow-hidden"
          />
        </div>
      )}
    </div>
  );
};

export default ChildReportsPage;


